/** Договор сделки — ровно то, что отдаёт `GET /api/deals/{id}/contract`. */
import { apiFetch } from "@/lib/api";
import type { BillingRow, BillingTask, DealBilling } from "@/lib/dealBilling";

type Who = { at: string | null; who: string | null };

/** Выдача товара по `sMove_deal`: «не возврат» — среди выданных. */
export type IssueState = "issued" | "returned" | "none";

/** Окно «История» товара — `GET /api/deals/{id}/contract/product/{row}/history`, новые сверху.
 *  Спека `docs/specs/2026-10-23-contract-issue-history-design.md`. */
export type ProductHistory = {
  row: number;
  serial: number | null;
  name: string;
  is_sale: boolean;
  state: string | null;
  issue: IssueState;
  moved: Who | null;
  /** Все сканы выдачи/возврата; `repeat` — повторный скан той же операции. */
  moves: { id: number; op: number; name: string; at: string | null; who: string | null; from: string | null; to: string | null; repeat: boolean }[];
  penalties: { at: string | null; who: string | null; status: string | null; sum: number }[];
  price: { at: string | null; who: string | null; old: number | null; new: number; type: string | null }[];
  edits: { at: string | null; who: string | null; field: string; old: string | null; new: string | null }[];
  /** Задачи перемещения серии в сделке, новые сверху; `actions` — действия по задаче, новые сверху. */
  tasks: (BillingTask & {
    place_to: string | null;
    place_to_loc: string | null;
    actions: { at: string | null; who: string | null; name: string; to: string | null; delay: string | null; comment: string | null }[];
  })[];
  /** Биллинг товара: премия строки и проводки задач серии, новые сверху. */
  billing: { total: DealBilling["total"]; rows: BillingRow[] } | null;
};

export const productHistoryQuery = (dealId: number, row: number) => ({
  queryKey: ["product-history", dealId, row],
  queryFn: () => apiFetch<ProductHistory>(`/api/deals/${dealId}/contract/product/${row}/history`),
  staleTime: 30_000,
});

/** Задача товара договора CRM (комментарий к серии с задачей) — как под карточкой товара в CRM;
 *  выполненных нет. Спека `docs/specs/2026-10-22-product-tasks-design.md`. */
export type ProductTask = {
  id: number;
  row: number;
  task: number;
  name: string;
  status: number;
  status_name: string | null;
  /** Цвет статуса задачи CRM. */
  color: string | null;
  text: string | null;
  who: string | null;
  at: string | null;
  deal: number | null;
  previews: string[];
  images: string[];
};

/** Задачи, которые сотрудник КЦ ставит товару сам, — порядок кнопок как назвал владелец. */
export const PRODUCT_TASKS: { id: number; name: string }[] = [
  { id: 25, name: "Обещали" },
  { id: 14, name: "Ремонт нужен" },
  { id: 3, name: "Химчистка нужна" },
  { id: 2, name: "Подшив нужен" },
];

export type ContractProduct = {
  id: number;
  serial: number | null;
  name: string;
  category: string | null;
  is_sale: boolean;
  is_select: boolean;
  service: boolean;
  /** Фото серии (у услуг нет). */
  photo: string | null;
  price: number;
  price_type: string | null;
  price_color: string | null;
  tailor: { status: string | null; who: string | null; delayed: string | null } | null;
  comment: string | null;
  customer_id: number | null;
  added: Who;
  /** Где товар по `sMove_deal`: «выдан» / «возвращён» / «не возврат», null — не двигался. */
  state: string | null;
  /** Вкладка «Выдано / Не выдано / Возвращено»; старый сервер поля не отдаёт. */
  issue?: IssueState;
  /** Кто и когда выдал / вернул последним. */
  moved?: Who | null;
  deleted: Who | null;
  /** Старый сервер поля не отдаёт. */
  tasks?: ProductTask[];
};

export type Contract = {
  id: number;
  status: string | null;
  status_color: string | null;
  category: string | null;
  group: string | null;
  fine: string | null;
  city: string | null;
  /** Город выдачи коротко («МСК») и салон договора (`locationID`); старый сервер — нет. */
  city_short?: string | null;
  location_id?: number | null;
  customer: { id: number | null; name: string | null; phone?: string | null };
  /** «Оплачено дней» договора: есть — в CRM стоит «Неточная дата». */
  days_paid?: number | null;
  rent: { start: string | null; end: string | null };
  deal_rent: { start: string | null; end: string | null };
  created: Who;
  updated: Who;
  printed: string | null;
  synced: string | null;
  sync_stale: boolean;
  status_changed: string | null;
  docs: string[];
  money: { total: number; positions: number };
  products: ContractProduct[];
  warnings: string[];
};

export type DealContract = {
  contracts: Contract[];
  money: { total: number; paid: number; due: number; percent: number | null };
};

export function loadDealContract(id: number): Promise<DealContract> {
  return apiFetch<DealContract>(`/api/deals/${id}/contract`);
}

/** Поставить задачу товару договора — **пишет в боевую CRM**. */
export const putProductTask = (dealId: number, row: number, task: number, text: string, chatId?: number | null) =>
  apiFetch<{ id: number; row: number; task: number; name: string; text: string }>(
    `/api/deals/${dealId}/contract/product/${row}/task`,
    { method: "POST", body: JSON.stringify({ task, text, chat_id: chatId || null }) },
  );
